import { AfterViewInit, ChangeDetectorRef, Component, OnInit , signal} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import momentTimeZone from 'moment-timezone';
import { BehaviorSubject, of, Subscription } from 'rxjs';
import { CommonUtil } from '../../../../../core/utils/common-util';
import { DateUtil } from '../../../../../core/utils/date-util';
import { HandoverNotesService } from '../../../../../state/modules/handover-notes/handover-notes.service';
import { HANDOVER_NOTES_ACTION_FORM_CONTROLS } from './handover-notes-action.constant';

@Component({
  standalone: false,
  selector: 'kt-handover-notes-action',
  templateUrl: './handover-notes-action.component.html',
  styleUrls: ['./handover-notes-action.component.scss'],
})
export class HandoverNotesActionComponent implements OnInit, AfterViewInit {

  isEdit;
  editHandoverNote;
  clientId;
  shiftDetail;
  loading = signal(false);
  form: any = new UntypedFormGroup({});
  subscriptions: Subscription[] = [];
  formControl = HANDOVER_NOTES_ACTION_FORM_CONTROLS;
  userId;

  lookup = {
    client: new BehaviorSubject<any[]>([]),
    location: new BehaviorSubject<any[]>([]),
    area: new BehaviorSubject<any[]>([])
  };

  constructor(
    public activeModal: NgbActiveModal,
    public handoverNotesService: HandoverNotesService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit() {
    this.getClientLookup();
  }

  ngAfterViewInit() {
    this.editModeData();
  }

  /**
   * It will setValue and disable the  Form Fields Whilw In Edit mode
   */
  editModeData() {
    if (this.editHandoverNote) {
      this.form.get('hand_over_note_text').setValue(this.editHandoverNote.hand_over_note_text);
      this.form.get('hand_over_note_fk_client_id').setValue(this.editHandoverNote.hand_over_note_fk_client_id);
      this.form.get('hand_over_note_fk_location_id').setValue(this.editHandoverNote.hand_over_note_fk_location_id);
      if(this.editHandoverNote.hand_over_note_area.length) {
        this.form.get('hand_over_note_area_ids').setValue(this.editHandoverNote.hand_over_note_area[0].hand_over_note_area_fk_area_id);
      }
      this.form.get('hand_over_note_expired_at').setValue(DateUtil.parse(this.editHandoverNote.hand_over_note_expired_at));

      setTimeout(() => {
        if (this.editHandoverNote.hand_over_note_upload_1) {
          this.form.get('file_upload').push(this.setGroup({ path: this.editHandoverNote.hand_over_note_upload_1, type: this.editHandoverNote.hand_over_note_upload_1_extension }));
        }
      }, 100);
      this.form.get('hand_over_note_fk_client_id').disable();
      this.form.get('hand_over_note_fk_location_id').disable();
      this.form.get('hand_over_note_area_ids').disable();
      // this.form.get('hand_over_note_expired_at').disable();
      this.form.get('file_upload').disable();
    } else {
      this.form.get('hand_over_note_expired_at').setValue(DateUtil.parse(moment().add(7, 'days').format()));
    }
  }

  /**
   * Creates new Formgroup for file_upload with controls path and type
   * @param data
   * @returns new UntypedFormGroup
   */
  setGroup(data) {
    return new UntypedFormGroup({
      path: new UntypedFormControl(data.path),
      type: new UntypedFormControl(data.type),
      status: new UntypedFormControl('Uploaded'),
    });
  }

  /**
   * To get Client List assigned to particular user
   */
  getClientLookup() {
    this.subscriptions.push(this.handoverNotesService.getClientLookup(this.userId).subscribe((data: any) => {
      if (data && data.data && data.data.data && data.data.data.clients) {
        this.lookup.client.next(data.data.data.clients);
      }
      if (this.shiftDetail && this.shiftDetail.location && this.shiftDetail.location.location_id) {
        this.getLocationLookup(this.shiftDetail.client.client_id);
      } else if (this.isEdit && this.editHandoverNote) {
        this.getLocationLookup(this.editHandoverNote.hand_over_note_fk_client_id);
      }
      this.loading.set(false);
    }, err => {
      this.loading.set(false);
    }));
  }

  /**
   * To get available location data
   * @param data It will have clientId
   */
  getLocationLookup(data) {
    this.loading.set(true);
    const payload = {
      clientId: data,
      userId: this.userId
    }
    this.clientId = data;
    this.subscriptions.push(this.handoverNotesService.getLocationLookup(payload).subscribe((data: any) => {
      if (data && data.data && data.data.data && data.data.data.location) {
        this.lookup.location.next(data.data.data.location);
        this.cdr.detectChanges();
      }
      if (this.shiftDetail && this.shiftDetail.area && this.shiftDetail.area.area_id) {
        this.getAreaLookup(this.shiftDetail.location.location_id);
      } else if (this.isEdit && this.editHandoverNote) {
        this.getAreaLookup(this.editHandoverNote.hand_over_note_fk_location_id);
      }
      this.loading.set(false);
    }, err => {
      this.loading.set(false);
    }));
  }

  /**
   * To get available area data
   * @param data It will have clientId and locationId
   */
  getAreaLookup(locationId) {
    const payload = {
      clientId: this.clientId,
      locationId: locationId,
    };

    this.subscriptions.push(this.handoverNotesService.getAreaLookup(payload).subscribe((data: any) => {
      if (data && data.data && data.data.data && data.data.data.areas) {
        this.lookup.area.next(data.data.data.areas);
        this.cdr.detectChanges();
      }
      this.loading.set(false);
    }, err => {
      this.loading.set(false);
    }));
  }

  /**
   * It will be called when user changes client value or location value
   * @param event client_id or location_id
   */
  valueChangeHandler(event) {
    if (event && event.name && event.name === 'hand_over_note_fk_client_id') {
      this.form.get('hand_over_note_fk_location_id').reset();
      this.form.get('hand_over_note_area_ids').reset();
      this.getLocationLookup(event.value);
    }
    if (event && event.name && event.name === 'hand_over_note_fk_location_id') {
      this.form.get('hand_over_note_area_ids').reset();
      this.getAreaLookup(event.value);
    }
  }

  /**
   * It will be called when user clicks on submit in add or edit Handover note popup
   */
  submitClickHandler() {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(controlName =>
        this.form.controls[controlName].markAsTouched(),
      );
      return;
    }
    this.loading.set(true);
    const payload = this.form.value;
    if (payload && payload.file_upload && payload.file_upload.length) {
      payload.hand_over_note_upload_1 = payload.file_upload[0].path;
      payload.hand_over_note_upload_1_extension = payload.file_upload[0].type;
    }
    if (payload && payload.hand_over_note_area_ids) {
      payload.hand_over_note_area_ids = [payload.hand_over_note_area_ids];
    } else {
      payload.hand_over_note_area_ids = [];
    }

    if (payload && payload.hand_over_note_expired_at) {
      payload.hand_over_note_expired_at = this.prepareExpiryDate(payload.hand_over_note_expired_at);
      // payload.hand_over_note_expired_at = DateUtil.ngbDateToUtc(payload.hand_over_note_expired_at);
    }

    payload.hand_over_note_text = CommonUtil.replaceEditorText(payload.hand_over_note_text);

    if (this.isEdit && this.editHandoverNote) {
      this.loading.set(true);
      payload.hand_over_note_id = this.editHandoverNote.hand_over_note_id;
      this.subscriptions.push(this.handoverNotesService.updateHandoverNote(payload).subscribe(data => {
        if (data && data.success) {
          this.activeModal.close('Submit');
        }
        this.loading.set(false);
      }));
    } else {
      this.subscriptions.push(this.handoverNotesService.addHandoverNote(payload).subscribe((data: any) => {
        if (data && data.data && data.data.data) {
          this.activeModal.close('Submit');
        }
        this.loading.set(false);
      }, err => {
        this.loading.set(false);
      }));
    }
  }

  prepareExpiryDate(date) {

    return DateUtil.convertNgbDateToUtcOnlyDateFormat(date) ? DateUtil.convertNgbDateToUtcOnlyDateFormat(date) + 'T23:59:00' : '';
  }
}
